import { useEffect, useState } from "react";
import {
  formatMarkupTimestamp,
  orderMarkupItems,
} from "@dialogueconsulting/sked-preview";
import type { MarkupValue } from "@dialogueconsulting/sked-preview";
import { Button } from "components/ui/button";
import { cn } from "@/lib/utils";

export function MarkupList({
  value,
  title = "Annotations",
  selectedItemId,
  onSelect,
  onEdit,
  onRemove,
  resolution,
  onResolve,
  disabled,
  collapsible = false,
}: {
  value: Pick<MarkupValue, "annotations" | "highlights">;
  title?: string;
  selectedItemId?: string | null;
  onSelect?: (id: string) => void;
  onEdit?: (id: string) => void;
  onRemove?: (id: string) => void;
  resolution?: { resolved: boolean; actorName: string };
  onResolve?: (resolved: boolean) => void;
  disabled?: boolean;
  collapsible?: boolean;
}) {
  const [expanded, setExpanded] = useState(!resolution?.resolved);
  useEffect(() => setExpanded(!resolution?.resolved), [resolution?.resolved]);
  const canCollapse = collapsible && Boolean(resolution?.resolved);
  const items = orderMarkupItems(value);
  return (
    <div
      className={cn(
        "rounded-lg bg-neutral-25 p-3 text-neutral-800",
        collapsible ? "text-xs leading-4" : "text-sm leading-5"
      )}
      data-testid="markup-list"
    >
      {canCollapse ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          className="mb-2 flex items-center gap-2 border-0 bg-transparent p-0 text-xs font-semibold leading-4"
        >
          {title}
          <i
            aria-hidden="true"
            className={cn(
              "fa-regular",
              expanded ? "fa-chevrons-up" : "fa-chevrons-down"
            )}
          />
        </button>
      ) : (
        <div className={cn("font-semibold", onEdit ? "mb-2.5" : "mb-2")}>
          {title}
        </div>
      )}
      {(!canCollapse || expanded) && (
        <div className={onEdit ? "space-y-2.5" : "space-y-2"}>
          {items.map((entry, index) => {
            const item =
              entry.kind === "annotation" ? entry.annotation : entry.highlight;
            const isPin = entry.kind === "annotation";
            const timestamp = isPin
              ? entry.annotation.timestampSeconds
              : undefined;
            return (
              <div
                key={item.id}
                className={cn(
                  "flex items-center rounded-lg",
                  onEdit ? "gap-0" : "gap-2",
                  onEdit || (!collapsible && index === 0)
                    ? "min-h-6"
                    : "min-h-5",
                  selectedItemId === item.id && "bg-blue-50"
                )}
              >
                <button
                  type="button"
                  disabled={!onSelect}
                  onClick={() => onSelect?.(item.id)}
                  className={cn(
                    "flex min-w-0 flex-1 gap-2 border-0 bg-transparent !p-0 text-left font-normal disabled:opacity-100",
                    onEdit ? "items-center" : "items-start",
                    collapsible ? "text-xs leading-4" : "text-sm leading-5"
                  )}
                >
                  <span className="inline-flex min-w-10 shrink-0">
                    <span
                      className={cn(
                        "inline-flex h-5 items-center justify-center gap-1 rounded px-1.5 text-xs font-semibold leading-4",
                        isPin
                          ? "bg-blue-50 text-blue-500"
                          : "bg-yellow-25 text-yellow-600"
                      )}
                    >
                      <i
                        className={cn(
                          "fa-regular inline-flex size-[15px] items-center justify-center text-xs",
                          isPin ? "fa-location-dot" : "fa-highlighter-line"
                        )}
                        aria-hidden="true"
                      />
                      {entry.number}
                      {isPin && entry.annotation.pageNumber !== undefined && (
                        <span>· Page {entry.annotation.pageNumber}</span>
                      )}
                      {timestamp !== undefined && (
                        <span>· {formatMarkupTimestamp(timestamp)}</span>
                      )}
                    </span>
                  </span>
                  <span className="min-w-0 break-words">{item.comment}</span>
                </button>
                {onEdit && (
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={`Edit annotation ${entry.number}`}
                    onClick={() => onEdit(item.id)}
                    className="grid size-6 shrink-0 place-items-center rounded-lg border-0 bg-transparent p-1 hover:bg-blue-50"
                  >
                    <i
                      aria-hidden="true"
                      className="fa-regular fa-pen-to-square text-xs"
                    />
                  </button>
                )}
                {onRemove && (
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={`Remove annotation ${entry.number}`}
                    onClick={() => onRemove(item.id)}
                    className="grid size-6 shrink-0 place-items-center rounded-lg border-0 bg-transparent p-1 hover:bg-blue-50"
                  >
                    <i
                      aria-hidden="true"
                      className="fa-regular fa-xmark text-xs"
                    />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {(resolution?.resolved || onResolve) && (
        <div className="mt-2 flex items-center justify-end gap-2 text-xs leading-4">
          {resolution?.resolved && (
            <span className="mr-auto">
              Resolved by{" "}
              <strong className="font-semibold">{resolution.actorName}</strong>
            </span>
          )}
          {onResolve && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => onResolve(!resolution?.resolved)}
              className="h-6 rounded-lg px-2 py-1 text-xs"
            >
              {resolution?.resolved ? "Reopen" : "Resolve"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
